import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from './lib/auth'
import { portalFor } from './lib/roles'
import { useCapabilities } from './lib/capabilities'
import { PageLoader } from './components/ui'
import AppLayout from './components/AppLayout'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import Profile from './pages/Profile'
import NotFound from './pages/NotFound'
import Unsupported from './pages/Unsupported'

import MinerDashboard from './pages/miner/Dashboard'
import TasksHome from './pages/miner/TasksHome'
import MinerProduction from './pages/miner/Production'
import MinerKyc from './pages/miner/Kyc'
import MinerSafety from './pages/miner/Safety'
import MinerCompliance from './pages/miner/Compliance'
import Notifications from './pages/miner/Notifications'
import FindCooperative from './pages/miner/FindCooperative'

import CourseList from './pages/learning/CourseList'
import CourseDetail from './pages/learning/CourseDetail'
import ModuleViewer from './pages/learning/ModuleViewer'
import ModuleAssessment from './pages/learning/ModuleAssessment'
import FinalAssessment from './pages/learning/FinalAssessment'

import Permits from './pages/ops/Permits'
import Documents from './pages/ops/Documents'
import Training from './pages/ops/Training'
import DailySafetyReport from './pages/ops/DailySafetyReport'
import ExpenseNew from './pages/ops/ExpenseNew'
import Approvals from './pages/ops/Approvals'
import Dispatch from './pages/ops/Dispatch'
import DispatchDetail from './pages/ops/DispatchDetail'
import SharedWithMe from './pages/ops/SharedWithMe'
import DispatchClosure from './pages/ops/DispatchClosure'
import Sales from './pages/ops/Sales'
import Finance from './pages/ops/Finance'
import Staff from './pages/ops/Staff'
import StaffDetail from './pages/ops/StaffDetail'
import Payroll from './pages/ops/Payroll'
import SecurityGate from './pages/ops/SecurityGate'

import LeaderDashboard from './pages/leader/Dashboard'
import LeaderOnboarding from './pages/leader/Onboarding'
import LeaderProduction from './pages/leader/ProductionReview'
import LeaderKyc from './pages/leader/KycReview'
import LeaderSafety from './pages/leader/SafetyTriage'
import LeaderTeam from './pages/leader/Team'
import AllocationRequests from './pages/leader/AllocationRequests'
import MemberDetail from './pages/leader/MemberDetail'
import StaffBadge from './pages/leader/StaffBadge'
import LeaderSites from './pages/leader/Sites'
import SiteDetail from './pages/leader/SiteDetail'
import Organization from './pages/leader/Organization'
import Trucks from './pages/leader/Trucks'
import TruckDetail from './pages/leader/TruckDetail'
import Equipment from './pages/leader/Equipment'
import Loads from './pages/leader/Loads'
import LoadDetail from './pages/leader/LoadDetail'
import Expenses from './pages/leader/Expenses'
import Reports from './pages/leader/Reports'
import ReportDetail from './pages/leader/ReportDetail'

import GovDashboard from './pages/gov/Dashboard'
import GovSites from './pages/gov/Sites'
import GovInspections from './pages/gov/Inspections'
import GovNotices from './pages/gov/Notices'
import GovLicences from './pages/gov/Licences'
import GovProduction from './pages/gov/Production'
import GovUsers from './pages/gov/Users'
import GovUserDetail from './pages/gov/UserDetail'
import GovOrgDetail from './pages/gov/OrgDetail'
import GovUnallocatedMiners from './pages/gov/UnallocatedMiners'
import GovSiteRegister from './pages/gov/SiteRegister'
import GovSiteDetail from './pages/gov/SiteDetail'
import GovLandParcels from './pages/gov/LandParcels'
import GovLandParcelDetail from './pages/gov/LandParcelDetail'
import GovInspectionDetail from './pages/gov/InspectionDetail'
import GovChecklists from './pages/gov/Checklists'
import GovAuditTrail from './pages/gov/AuditTrail'
import GovCourseUploads from './pages/gov/CourseUploads'
import GovCourseUploadCreate from './pages/gov/CourseUploadCreate'
import GovCourseProgress from './pages/gov/CourseProgress'
import GovEquipment from './pages/gov/Equipment'

import AdminDashboard from './pages/admin/Dashboard'
import AdminUsers from './pages/admin/Users'
import AdminCreateUser from './pages/admin/CreateUser'
import AdminUserDetail from './pages/admin/UserDetail'
import AdminUserEdit from './pages/admin/UserEdit'
import AdminOrgDetail from './pages/admin/OrgDetail'
import AdminCreateOrganization from './pages/admin/CreateOrganization'
import AdminOrganizations from './pages/admin/Organizations'
import AdminDeletedOrganizations from './pages/admin/DeletedOrganizations'
import AdminCompliance from './pages/admin/Compliance'
import AdminComplianceDetail from './pages/admin/ComplianceDetail'
import AdminApprovals from './pages/admin/Approvals'
import AdminApprovalDetail from './pages/admin/ApprovalDetail'
import AdminRoleCatalog from './pages/admin/RoleCatalog'
import AdminRoleCatalogEdit from './pages/admin/RoleCatalogEdit'
import AdminRoles from './pages/admin/Roles'
import AdminLoads from './pages/admin/Loads'
import CommunityLevy from './pages/admin/CommunityLevy'
import AdminSites from './pages/admin/Sites'
import AdminPermissions from './pages/admin/Permissions'
import AdminHiredEquipment from './pages/admin/HiredEquipment'
import AdminPrivateUsers from './pages/admin/PrivateUsers'
import AdminUsersMap from './pages/admin/UsersMap'
import AdminEquipmentGuide from './pages/admin/EquipmentGuide'
import AdminEquipmentGuideDetail from './pages/admin/EquipmentGuideDetail'
import AdminRoleForm from './pages/admin/RoleForm'

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <PageLoader label="Checking your session…" />
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />
  return <>{children}</>
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  if (user) return <Navigate to="/" replace />
  return <>{children}</>
}

/* Route table is chosen by role rather than nested under /miner and /leader
 * prefixes: each user only ever has one portal, so keeping the paths identical
 * means bookmarks and the PWA shortcuts work for everyone. */
function PortalRoutes() {
  const { user } = useAuth()
  const portal = portalFor(user)

  if (portal === 'unsupported') return <Unsupported />

  if (portal === 'gov') {
    return (
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<GovDashboard />} />
          <Route path="sites" element={<GovSites />} />
          <Route path="sites/register" element={<GovSiteRegister />} />
          <Route path="sites/:siteId" element={<GovSiteDetail />} />
          <Route path="parcels" element={<GovLandParcels />} />
          <Route path="parcels/:parcelId" element={<GovLandParcelDetail />} />
          <Route path="inspections" element={<GovInspections />} />
          <Route path="inspections/:inspectionId" element={<GovInspectionDetail />} />
          <Route path="notices" element={<GovNotices />} />
          <Route path="licences" element={<GovLicences />} />
          <Route path="production" element={<GovProduction />} />
          <Route path="users" element={<GovUsers />} />
          <Route path="users/:uid" element={<GovUserDetail />} />
          <Route path="organizations/:orgId" element={<GovOrgDetail />} />
          <Route path="unallocated-miners" element={<GovUnallocatedMiners />} />
          <Route path="checklists" element={<GovChecklists />} />
          {/* Not linked from nav: matches the Jinja original's super_admin-only
              gate, and super_admin never reaches the React gov portal at all
              (portalFor() sends them to the Jinja app) - kept reachable by
              direct URL only, in case that ever changes. */}
          <Route path="audit-trail" element={<GovAuditTrail />} />
          <Route path="course-uploads" element={<GovCourseUploads />} />
          <Route path="course-uploads/new" element={<GovCourseUploadCreate />} />
          <Route path="course-progress" element={<GovCourseProgress />} />
          <Route path="equipment" element={<GovEquipment />} />
          <Route path="profile" element={<Profile />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    )
  }

  if (portal === 'leader') {
    return (
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<LeaderDashboard />} />
          <Route path="onboarding" element={<LeaderOnboarding />} />
          <Route path="production" element={<LeaderProduction />} />
          <Route path="kyc" element={<LeaderKyc />} />
          <Route path="safety" element={<LeaderSafety />} />
          <Route path="team" element={<LeaderTeam />} />
          <Route path="allocation-requests" element={<AllocationRequests />} />
          <Route path="team/:uid" element={<MemberDetail />} />
          <Route path="team/:uid/badge" element={<StaffBadge />} />
          <Route path="sites" element={<LeaderSites />} />
          <Route path="sites/:siteId" element={<SiteDetail />} />
          <Route path="organization" element={<Organization />} />
          <Route path="trucks" element={<Trucks />} />
          <Route path="trucks/:truckId" element={<TruckDetail />} />
          <Route path="equipment" element={<Equipment />} />
          <Route path="loads" element={<Loads />} />
          <Route path="loads/:loadId" element={<LoadDetail />} />
          <Route path="expenses" element={<Expenses />} />
          <Route path="reports" element={<Reports />} />
          <Route path="reports/:reportId" element={<ReportDetail />} />
          <Route path="learning" element={<CourseList />} />
          <Route path="learning/:courseId" element={<CourseDetail />} />
          <Route path="learning/:courseId/assessment" element={<FinalAssessment />} />
          <Route path="learning/:courseId/modules/:moduleIndex" element={<ModuleViewer />} />
          <Route path="learning/:courseId/modules/:moduleIndex/assessment" element={<ModuleAssessment />} />
          <Route path="compliance" element={<MinerCompliance />} />
          <Route path="permits" element={<Permits />} />
          <Route path="safety-documents" element={<Documents />} />
          <Route path="training-manage" element={<Training />} />
          <Route path="daily-safety-report" element={<DailySafetyReport />} />
          <Route path="dispatch" element={<Dispatch />} />
          <Route path="dispatch/:loadId" element={<DispatchDetail />} />
          <Route path="sales" element={<Sales />} />
          <Route path="finance" element={<Finance />} />
          <Route path="hr" element={<Staff />} />
          <Route path="hr/payroll" element={<Payroll />} />
          <Route path="hr/:uid" element={<StaffDetail />} />
          <Route path="gate" element={<SecurityGate />} />
          <Route path="approvals" element={<Approvals />} />
          <Route path="shared-with-me" element={<SharedWithMe />} />
          <Route path="notifications" element={<Notifications />} />
          <Route path="profile" element={<Profile />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    )
  }

  if (portal === 'admin') {
    return (
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/new" element={<AdminCreateUser />} />
          <Route path="users/:uid" element={<AdminUserDetail />} />
          <Route path="users/:uid/edit" element={<AdminUserEdit />} />
          <Route path="organizations" element={<AdminOrganizations />} />
          <Route path="organizations/new" element={<AdminCreateOrganization />} />
          <Route path="organizations/:orgId" element={<AdminOrgDetail />} />
          {/* Not linked from ADMIN_NAV (mirrors the gov portal's audit-trail
              route, which is also reachable by direct URL only) - the Jinja
              original surfaces this as its own "Deleted Orgs" sidebar item
              (base.html) rather than a link from the Organisations page
              itself, so there's no single obvious spot to link it from here
              yet either. Revisit once org_detail's own "Deleted
              Organisations" link (org_detail.html) has a React home. */}
          <Route path="organizations/deleted" element={<AdminDeletedOrganizations />} />
          <Route path="compliance" element={<AdminCompliance />} />
          <Route path="compliance/:orgId" element={<AdminComplianceDetail />} />
          <Route path="approvals" element={<AdminApprovals />} />
          <Route path="approvals/:requestId" element={<AdminApprovalDetail />} />
          <Route path="role-catalog" element={<AdminRoleCatalog />} />
          <Route path="role-catalog/:roleKey" element={<AdminRoleCatalogEdit />} />
          <Route path="roles" element={<AdminRoles />} />
          <Route path="roles/new" element={<AdminRoleForm mode="create" />} />
          <Route path="roles/:key/edit" element={<AdminRoleForm mode="edit" />} />
          {/* HR/Finance already have a platform-wide "universal viewer" mode
              server-side (accessible_org_ids() returns None for super_admin,
              and the finance/payroll pages already carry an org picker) - so
              these are the same leader-portal components, just newly wired
              into the admin route tree. */}
          <Route path="hr" element={<Staff />} />
          <Route path="hr/payroll" element={<Payroll />} />
          <Route path="hr/:uid" element={<StaffDetail />} />
          <Route path="finance" element={<Finance />} />
          <Route path="community-levy" element={<CommunityLevy />} />
          <Route path="permissions" element={<AdminPermissions />} />
          <Route path="dispatch-closure" element={<DispatchClosure />} />
          {/* Clicking a card in the closure queue opens that load -
              without this the admin portal 404'd on its own links. */}
          <Route path="dispatch/:loadId" element={<DispatchDetail />} />
          <Route path="sites" element={<AdminSites />} />
          {/* Same site-detail screen the cooperative portal uses - GET
              /sites/:id is scope-checked server-side, and a super admin's
              scope is the whole platform. */}
          <Route path="sites/:siteId" element={<SiteDetail />} />
          <Route path="loads" element={<AdminLoads />} />
          <Route path="trucks" element={<Trucks />} />
          <Route path="trucks/:truckId" element={<TruckDetail />} />
          <Route path="expenses" element={<Expenses />} />
          <Route path="hired-equipment" element={<AdminHiredEquipment />} />
          <Route path="learning" element={<CourseList />} />
          <Route path="learning/:courseId" element={<CourseDetail />} />
          <Route path="learning/:courseId/assessment" element={<FinalAssessment />} />
          <Route path="learning/:courseId/modules/:moduleIndex" element={<ModuleViewer />} />
          <Route path="learning/:courseId/modules/:moduleIndex/assessment" element={<ModuleAssessment />} />
          <Route path="course-progress" element={<GovCourseProgress />} />
          <Route path="private" element={<AdminPrivateUsers />} />
          <Route path="unallocated-miners" element={<GovUnallocatedMiners />} />
          <Route path="users-map" element={<AdminUsersMap />} />
          <Route path="equipment-guide" element={<AdminEquipmentGuide />} />
          <Route path="equipment-guide/:equipmentId" element={<AdminEquipmentGuideDetail />} />
          <Route path="profile" element={<Profile />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    )
  }

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<MinerHome />} />
        <Route path="production" element={<RequireCap cap="management.senior"><MinerProduction /></RequireCap>} />
        <Route path="kyc" element={<MinerKyc />} />
        <Route path="safety" element={<MinerSafety />} />
        <Route path="compliance" element={<MinerCompliance />} />
        <Route path="find-cooperative" element={<FindCooperative />} />
        <Route path="learning" element={<CourseList />} />
        <Route path="learning/:courseId" element={<CourseDetail />} />
        <Route path="learning/:courseId/assessment" element={<FinalAssessment />} />
        <Route path="learning/:courseId/modules/:moduleIndex" element={<ModuleViewer />} />
        <Route path="learning/:courseId/modules/:moduleIndex/assessment" element={<ModuleAssessment />} />
        <Route path="permits" element={<RequireCap cap="permits.manage"><Permits /></RequireCap>} />
        <Route path="safety-documents" element={<RequireCap cap="documents.manage"><Documents /></RequireCap>} />
        <Route path="training-manage" element={<RequireCap cap="training.manage"><Training /></RequireCap>} />
        <Route path="daily-safety-report" element={<RequireCap cap="report.daily_safety"><DailySafetyReport /></RequireCap>} />
        <Route path="dispatch" element={<RequireCap cap="dispatch.manage"><Dispatch /></RequireCap>} />
        <Route path="dispatch/:loadId" element={<DispatchDetail />} />
        <Route path="dispatch-closure" element={<RequireCap cap="management.senior"><DispatchClosure /></RequireCap>} />
        {/* Dispatchers record equipment, including on another
            cooperative's behalf - see api_leader_equipment_create. */}
        <Route path="equipment" element={<RequireCap cap="dispatch.manage"><Equipment /></RequireCap>} />
        <Route path="log-expense" element={<RequireCap cap="expense.log"><ExpenseNew /></RequireCap>} />
        <Route path="sales" element={<RequireCap cap="sales.log"><Sales /></RequireCap>} />
        <Route path="finance" element={<RequireCap cap="reports.view.sensitive"><Finance /></RequireCap>} />
        <Route path="hr" element={<RequireCap cap="hr.view"><Staff /></RequireCap>} />
        <Route path="hr/payroll" element={<RequireCap cap="hr.manage"><Payroll /></RequireCap>} />
        <Route path="hr/:uid" element={<StaffDetail />} />
        <Route path="gate" element={<RequireCap cap="security.gate_verify"><SecurityGate /></RequireCap>} />
        <Route path="approvals" element={<RequireCap cap="approvals.review"><Approvals /></RequireCap>} />
        {/* Same reports screen the cooperative portal uses - operational
            roles (project_manager, data_analyst, …) hold the report
            capabilities but had no screen to read them on. */}
        <Route path="reports" element={<RequireCap cap="reports.view.operations"><Reports /></RequireCap>} />
        <Route path="reports/:reportId" element={<ReportDetail />} />
        <Route path="shared-with-me" element={<SharedWithMe />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="profile" element={<Profile />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}

/* A route the menu hides must also be unreachable.
 *
 * Capability gating used to live only in the sidebar, so typing the URL (or
 * following an old link) opened a screen the person had no business on - it
 * would render, then the API would refuse and they'd get an error about
 * permissions. Hiding something and then explaining why it's forbidden is
 * the worst of both. This mirrors the nav's `cap` exactly: no capability,
 * no route - quietly back to their home instead of a refusal.
 *
 * Capabilities load asynchronously, so hold the render until they arrive;
 * redirecting on an empty set would bounce everyone on first paint. */
function RequireCap({ cap, children }: { cap: string; children: ReactNode }) {
  const caps = useCapabilities()
  const { user } = useAuth()
  if (!user) return null
  if (caps.size === 0) return <PageLoader />
  if (!caps.has('*') && !caps.has(cap)) return <Navigate to="/" replace />
  return <>{children}</>
}

/* Which home an operational account opens on.
 *
 * The production dashboard belongs to whoever RUNS the cooperative - its
 * leadership, the project manager, the platform administrators - all of whom
 * carry `management.senior`. Everyone else gets a home listing the tools
 * their role actually holds (TasksHome), because they open the app to do
 * their job, not to read output figures. */
function MinerHome() {
  const caps = useCapabilities()
  const senior = caps.has('*') || caps.has('management.senior')
  return senior ? <MinerDashboard /> : <TasksHome />
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
      <Route path="/forgot-password" element={<RedirectIfAuthed><ForgotPassword /></RedirectIfAuthed>} />
      <Route path="/*" element={<RequireAuth><PortalRoutes /></RequireAuth>} />
    </Routes>
  )
}
